import React from 'react';

import { cn } from './cn';

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  /** Accessible name when no visible `label` is rendered (e.g. table row selection). */
  ariaLabel?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  className?: string;
  id?: string;
  hasError?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked,
      onChange,
      label,
      ariaLabel,
      disabled,
      indeterminate,
      className,
      id,
      hasError,
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const checkboxRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
    const generatedId = React.useId();
    const checkboxId = id || generatedId;

    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="relative flex items-center">
          <input
            ref={checkboxRef}
            id={checkboxId}
            type="checkbox"
            checked={controlledChecked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            aria-label={!label ? ariaLabel : undefined}
            className={cn(
              'peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border bg-surface transition-colors duration-150',
              'border-border-strong',
              'checked:border-status-info-emphasis checked:bg-status-info-emphasis',
              'indeterminate:border-status-info-emphasis indeterminate:bg-status-info-emphasis',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-500/60 focus-visible:ring-offset-2',
              'dark:focus-visible:ring-offset-surface-raised',
              hasError && 'border-danger-500 dark:border-danger-400',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          />
          <svg
            viewBox="0 0 12 12"
            className="pointer-events-none absolute left-0 top-0 h-4 w-4 text-text-on-info opacity-0 peer-checked:opacity-100 transition-opacity duration-150"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M2 6l3 3 5-5" />
          </svg>
        </div>
        {label && (
          <label
            htmlFor={checkboxId}
            // Disabled labels mute one step instead of `opacity-50` — halving a light text color on
            // a dark surface drops it below legibility, and the label must stay readable.
            className={cn(
              'text-sm select-none',
              disabled ? 'text-text-secondary cursor-not-allowed' : 'text-text-primary cursor-pointer'
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);
Checkbox.displayName = 'Checkbox';
